import { type ModelDataType, MODEL_DATA_TYPES } from '@ddp/shared';

import type { MessageKey } from '../../i18n/index.js';

/**
 * Catalogue curé des **cas d'usage** de la page Model (ADR-0017). Les données vivent en dur côté
 * front ; leur passage à une entité cloisonnée éditable est différé (ADR-0017 §3).
 *
 * La **bibliothèque de modèles pré-entraînés** ne vit plus ici : elle est servie par l'API
 * (`GET /model-library`, ADR-0018), curation + métadonnées récupérées d'une plateforme tierce.
 * Le type `ModelDataType` est désormais partagé (`@ddp/shared`) pour que back et front s'accordent.
 */

export { MODEL_DATA_TYPES };
export type { ModelDataType };

/** Clé i18n du libellé d'un type de données. */
export const DATA_TYPE_LABEL_KEY: Record<ModelDataType, MessageKey> = {
  OPTICAL: 'model.dataType.optical',
  IR: 'model.dataType.ir',
  ACOUSTIC: 'model.dataType.acoustic',
  RADAR: 'model.dataType.radar',
};

/** Un cas d'usage opérationnel du catalogue de base. */
export interface UseCaseEntry {
  id: string;
  nameKey: MessageKey;
  descKey: MessageKey;
  /** Types de données pertinents — oriente vers la bonne famille de modèles. */
  dataTypes: readonly ModelDataType[];
}

/**
 * Catalogue de base des cas d'usage (ADR-0017). L'utilisateur pourra en ajouter d'autres en
 * incrément 2 (entité cloisonnée + anti-doublon) ; cette base reste le point de départ commun.
 */
export const USE_CASE_CATALOG: readonly UseCaseEntry[] = [
  {
    id: 'fpv',
    nameKey: 'model.useCase.fpv.name',
    descKey: 'model.useCase.fpv.desc',
    dataTypes: ['OPTICAL', 'ACOUSTIC'],
  },
  {
    id: 'shahed',
    nameKey: 'model.useCase.shahed.name',
    descKey: 'model.useCase.shahed.desc',
    dataTypes: ['OPTICAL', 'IR', 'ACOUSTIC'],
  },
  {
    id: 'lightVehicle',
    nameKey: 'model.useCase.lightVehicle.name',
    descKey: 'model.useCase.lightVehicle.desc',
    dataTypes: ['OPTICAL', 'IR'],
  },
  {
    id: 'objectTracking',
    nameKey: 'model.useCase.objectTracking.name',
    descKey: 'model.useCase.objectTracking.desc',
    dataTypes: ['OPTICAL', 'IR'],
  },
  {
    id: 'terrain',
    nameKey: 'model.useCase.terrain.name',
    descKey: 'model.useCase.terrain.desc',
    dataTypes: ['OPTICAL', 'IR', 'RADAR'],
  },
  {
    id: 'personnel',
    nameKey: 'model.useCase.personnel.name',
    descKey: 'model.useCase.personnel.desc',
    dataTypes: ['OPTICAL', 'IR'],
  },
];

/** Nom i18n d'un cas d'usage par son id — sert de « ce qu'il détecte » sur les fiches de modèle. */
export const USE_CASE_NAME_KEY: Record<string, MessageKey> = Object.fromEntries(
  USE_CASE_CATALOG.map((useCase) => [useCase.id, useCase.nameKey]),
);
